import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import styles from './Field.module.css';

interface BaseProps {
  label: string;
  help?: ReactNode;
  error?: string | null;
}

/**
 * 입력 한 칸.
 *
 * 라벨·도움말·오류를 `aria-describedby`로 묶는다 — 스크린 리더가 입력에
 * 들어갔을 때 "왜 틀렸는지"까지 읽어준다. 시각적으로만 빨갛게 하면
 * 그 정보가 전달되지 않는다.
 */
export function Field({
  label,
  help,
  error,
  prefix,
  ...rest
}: BaseProps & { prefix?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const invalid = error !== null && error !== undefined && error !== '';

  const describedBy = [help !== undefined ? helpId : '', invalid ? errorId : '']
    .filter(Boolean)
    .join(' ');

  const input = (
    <input
      id={id}
      className={[styles.control, invalid && prefix === undefined ? styles.invalid : '']
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      {...rest}
    />
  );

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>

      {prefix === undefined ? (
        input
      ) : (
        <div
          className={[styles.withPrefix, invalid ? styles.invalid : ''].filter(Boolean).join(' ')}
        >
          <span className={styles.prefix}>{prefix}</span>
          {input}
        </div>
      )}

      {help !== undefined ? (
        <p id={helpId} className={styles.help}>
          {help}
        </p>
      ) : null}
      {invalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextareaField({
  label,
  help,
  error,
  ...rest
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const invalid = error !== null && error !== undefined && error !== '';
  const describedBy = [help !== undefined ? helpId : '', invalid ? errorId : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className={[styles.control, styles.textarea, invalid ? styles.invalid : '']
          .filter(Boolean)
          .join(' ')}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        {...rest}
      />
      {help !== undefined ? (
        <p id={helpId} className={styles.help}>
          {help}
        </p>
      ) : null}
      {invalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
